import { useEffect, useMemo, useRef, useState } from "react";
import { useSpatial } from "./SpatialContext";
import { TYPE_COLORS } from "./FloatingTimer";
import { BlockPie } from "./timer/BlockPie";
import { blocksForDate, datedNotesForDate, dayEndForDate, eventsForDate, proposedBlocksForDate, toDateKey, timeToMinutes } from "../lib/plannerItems";
import { pendingPlanId } from "../lib/dayPlanProposal";
import { isGhostEvent } from "../lib/lumenCalendar";
import { layOutDay, maxLengthFor, minutesToTime as minsToTime, nextDateKey, returnSlot, rollOverStart, snap, staleBlocks, withPatches, MIN_BLOCK_MINUTES } from "../lib/dayPlan";

const LENGTHS = [15, 30, 45, 60, 90, 120];

// A block's height tracks how long it runs, so a 30 minute block reads as half of an
// hour one. The floor is what a row needs for its two lines of text.
const PX_PER_MIN = 0.9;
const MIN_ROW_HEIGHT = 40;
const heightForMinutes = (mins) => Math.max(MIN_ROW_HEIGHT, Math.round(mins * PX_PER_MIN));

const EVENT_COLOR = { bg: "#ECFDF5", border: "#4DB8B5" };
const GHOST_EVENT_COLOR = { bg: "#F8FAFC", border: "#94A3B8" };
const NOTE_COLOR = { bg: "#FFFBEB", border: "#F59E0B" };
const PLAN_COLOR = { bg: "#F8FAFC", border: "#94A3B8" };

function DaySwitch({ showTomorrow, onChange, tomorrowHasPlan }) {
  const pill = (active) => ({
    flex: 1, padding: "4px 0", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 10, fontWeight: 700,
    fontFamily: "'DM Sans',sans-serif", background: active ? "white" : "transparent", color: active ? "#1E293B" : "#64748B",
    boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
  });
  return (
    <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 9, background: "#F1F5F9", marginBottom: 8 }}>
      <button type="button" style={pill(!showTomorrow)} onClick={() => onChange(false)}>Today</button>
      <button type="button" style={pill(showTomorrow)} onClick={() => onChange(true)}>
        Tomorrow{tomorrowHasPlan ? " •" : ""}
      </button>
    </div>
  );
}

const minutesToTime = (mins) => {
  const capped = Math.max(0, Math.min(23 * 60 + 45, mins));
  return `${String(Math.floor(capped / 60)).padStart(2, "0")}:${String(capped % 60).padStart(2, "0")}`;
};

const nextSlot = () => {
  const now = new Date();
  return minutesToTime(Math.ceil((now.getHours() * 60 + now.getMinutes()) / 15) * 15);
};

function NowLine({ time, innerRef }) {
  return (
    <div ref={innerRef} style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 6px" }}>
      <div style={{ width: 34, flexShrink: 0, textAlign: "right", fontSize: 9, fontWeight: 700, color: "#EF4444" }}>
        {time}
      </div>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#EF4444", flexShrink: 0 }} />
      <div style={{ flex: 1, height: 2, borderRadius: 1, background: "#EF4444", opacity: 0.6 }} />
    </div>
  );
}

const fieldStyle = {
  border: "1px solid #E2E8F0", borderRadius: 6, padding: "3px 5px", fontSize: 10,
  color: "#1E293B", fontFamily: "'DM Sans',sans-serif", outline: "none", background: "white",
};

function Row({ time, colors, children, onClick, title, faded, height }) {
  return (
    <div onClick={onClick} title={title}
      style={{
        display: "flex", gap: 8, padding: "6px 8px", borderRadius: 10, marginBottom: 4,
        background: colors.bg, border: `1.5px solid ${colors.border}33`,
        cursor: onClick ? "pointer" : "default", opacity: faded ? 0.5 : 1,
        height, boxSizing: "border-box", overflow: "hidden",
      }}>
      <div style={{ width: 34, flexShrink: 0, textAlign: "right", fontSize: 9, fontWeight: 600, color: "#94A3B8" }}>
        {time}
      </div>
      <div style={{ width: 2, borderRadius: 1, background: colors.border, flexShrink: 0, alignSelf: "stretch" }} />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

function BlockForm({ type, onSave, onCancel }) {
  const [title, setTitle] = useState("");
  const [start, setStart] = useState(nextSlot());
  const [length, setLength] = useState(type === "break" ? 15 : 60);
  const [error, setError] = useState(null);
  const colors = TYPE_COLORS[type] || TYPE_COLORS.task;

  const save = () => {
    if (!title.trim()) { setError("Give it a name."); return; }
    const result = onSave({ title, type, start_time: start, duration_minutes: length });
    if (result?.ok === false) { setError("That does not fit before midnight."); return; }
  };

  return (
    <div style={{ padding: "7px 8px", borderRadius: 10, marginBottom: 4, background: colors.bg, border: `1.5px solid ${colors.border}` }}>
      <input type="text" aria-label="Block name" autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
        placeholder={type === "break" ? "Break for..." : "What are you doing?"}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onCancel(); }}
        style={{ ...fieldStyle, width: "100%", marginBottom: 5 }} />
      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
        <input type="time" aria-label="Start time" value={start} onChange={(e) => setStart(e.target.value)}
          style={{ ...fieldStyle, flex: 1 }} />
        <select aria-label="Length" value={length} onChange={(e) => setLength(Number(e.target.value))}
          style={{ ...fieldStyle, flex: 1 }}>
          {LENGTHS.map((m) => <option key={m} value={m}>{m} min</option>)}
        </select>
      </div>
      {error && <div style={{ fontSize: 9, color: "#B91C1C", marginTop: 4 }}>{error}</div>}
      <div style={{ display: "flex", gap: 5, marginTop: 5 }}>
        <button onClick={save}
          style={{ flex: 1, padding: "3px 0", borderRadius: 6, border: "none", background: colors.border,
            color: "white", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
          Add
        </button>
        <button onClick={onCancel}
          style={{ padding: "3px 8px", borderRadius: 6, border: "1px solid #E2E8F0", background: "white",
            color: "#64748B", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function MyDayPanel({ activeBlockId, onActivateBlock, countdown }) {
  const {
    surfaceState, persistedSurfaceItems,
    addDayBlock, deleteDayBlock, toggleDayBlock, updateDayBlocks, setDayEnd, acceptDayPlan, dismissDayPlan,
  } = useSpatial();
  const [showTomorrow, setShowTomorrow] = useState(false);
  const [adding, setAdding] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [resizing, setResizing] = useState(null);
  const [rolledIn, setRolledIn] = useState(0);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [noticeHidden, setNoticeHidden] = useState(false);
  const nowLineRef = useRef(null);
  const rolledRef = useRef(false);

  useEffect(() => {
    const tick = () => setNow(new Date());
    const msToNextMinute = 60000 - (Date.now() % 60000);
    let interval;
    const timeout = setTimeout(() => { tick(); interval = setInterval(tick, 60000); }, msToNextMinute);
    return () => { clearTimeout(timeout); clearInterval(interval); };
  }, []);

  useEffect(() => {
    const line = nowLineRef.current;
    if (typeof line?.scrollIntoView === "function") line.scrollIntoView({ block: "center" });
  }, []);

  const todayKey = toDateKey();
  const viewKey = showTomorrow ? nextDateKey(todayKey) : todayKey;
  const isToday = viewKey === todayKey;
  const calendarEvents = surfaceState?.calendarEvents;
  const todayDayEnd = dayEndForDate(persistedSurfaceItems, todayKey);
  const dayEnd = dayEndForDate(persistedSurfaceItems, viewKey);
  const planId = pendingPlanId(persistedSurfaceItems, viewKey);
  const proposed = useMemo(() => proposedBlocksForDate(persistedSurfaceItems, viewKey), [persistedSurfaceItems, viewKey]);
  const previewing = !!planId;
  const tomorrowHasPlan = !!pendingPlanId(persistedSurfaceItems, nextDateKey(todayKey));

  // Anything left unfinished on an earlier day is carried into today, once per visit.
  useEffect(() => {
    if (rolledRef.current) return;
    const stale = staleBlocks(persistedSurfaceItems, todayKey);
    if (!stale.length) return;
    rolledRef.current = true;
    const patches = [];
    let carried = [...persistedSurfaceItems];
    for (const block of stale) {
      const start = rollOverStart(carried, todayKey, block.duration_minutes, todayDayEnd);
      patches.push({ id: block.id, date: todayKey, start_time: start });
      carried = carried.map((i) => (i.id === block.id ? { ...i, date: todayKey, start_time: start } : i));
    }
    updateDayBlocks(patches);
    setRolledIn(patches.length);
  }, [persistedSurfaceItems, todayKey, todayDayEnd, updateDayBlocks]);

  const items = useMemo(() => {
    const fromEvents = eventsForDate(calendarEvents || [], viewKey).map((e) => ({
      key: e.id, kind: "event", time: e.start_time, title: e.title, ghost: isGhostEvent(e),
      detail: isGhostEvent(e) ? `until ${e.end_time} · suggested, confirm it on the calendar` : `until ${e.end_time}`,
      minutes: timeToMinutes(e.end_time) - timeToMinutes(e.start_time),
    }));
    const fromNotes = datedNotesForDate(persistedSurfaceItems, viewKey).map(({ note, time }) => ({
      key: note.id, kind: "note", time, title: note.content, detail: "on the bulletin board", done: note.done,
    }));
    const fromBlocks = blocksForDate(persistedSurfaceItems, viewKey)
      .filter((b) => !previewing || b.done)
      .map((b) => ({ key: b.id, kind: "block", time: b.start_time, title: b.title, block: b }));
    const fromPlan = proposed.map((b) => ({ key: b.id, kind: "plan", time: b.start_time, title: b.title, block: b }));
    return [...fromEvents, ...fromNotes, ...fromBlocks, ...fromPlan]
      .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  }, [calendarEvents, persistedSurfaceItems, viewKey, previewing, proposed]);

  const replacedCount = previewing
    ? blocksForDate(persistedSurfaceItems, viewKey).filter((b) => !b.done && !proposed.some((p) => p.from_block === b.id)).length
    : 0;

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowLabel = minutesToTime(nowMinutes);
  const nowLineIndex = isToday ? items.findIndex((item) => timeToMinutes(item.time) > nowMinutes) : -2;
  // The end-of-day line sits among the items by time, so anything scheduled after it
  // shows below it. An item starting exactly when the day ends is already outside it.
  const dayEndMinutes = timeToMinutes(dayEnd);
  const dayEndIndex = items.findIndex((item) => timeToMinutes(item.time) >= dayEndMinutes);

  const dayEndLine = (
    <div key="day-end" title="Drag to set when your day ends" onMouseDown={(e) => startDayEndDrag(e)}
      style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0 6px", cursor: "ns-resize" }}>
      <div style={{ width: 34, flexShrink: 0, textAlign: "right", fontSize: 9, fontWeight: 700, color: "#1E293B" }}>
        {dayEnd}
      </div>
      <div style={{ flex: 1, height: 3, borderRadius: 2, background: "#1E293B", opacity: 0.75 }} />
      <span style={{ fontSize: 8, color: "#64748B", fontFamily: "'DM Sans',sans-serif" }}>day ends</span>
    </div>
  );

  // The now-line and the end-of-day line that belong just before item `index`
  // (or after everything, for -1), in the order their times fall.
  const markersBefore = (index) => {
    const markers = [];
    if (nowLineIndex === index && (items.length > 0 || index !== -1)) {
      markers.push({ at: nowMinutes, el: <NowLine key="now" time={nowLabel} innerRef={nowLineRef} /> });
    }
    if (dayEndIndex === index) markers.push({ at: dayEndMinutes, el: dayEndLine });
    return markers.sort((a, b) => a.at - b.at).map((m) => m.el);
  };

  const addBlock = (fields) => {
    const result = addDayBlock({ ...fields, date: viewKey });
    if (result?.ok !== false) setAdding(null);
    return result;
  };

  // Resizing changes one block's length; everything after it slides, events stay put,
  // and whatever no longer fits before the end of the day moves to tomorrow.
  const tomorrowKey = nextDateKey(viewKey);

  // Blocks that a stretch or an earlier end of day pushed to tomorrow. They carry where
  // they came from, so they come back by themselves once there is room again.
  const displacedBlocks = useMemo(
    () => persistedSurfaceItems.filter(
      (i) => i.kind === "day_block" && !i.proposal && i.date === tomorrowKey && i.displaced_from === viewKey && !i.done
    ),
    [persistedSurfaceItems, tomorrowKey, viewKey]
  );

  const reflowDay = (endTime, overrides = {}, protectedId = null) => {
    const displacedIds = new Set(displacedBlocks.map((b) => b.id));
    const { changed, overflow } = layOutDay(persistedSurfaceItems, viewKey, endTime, overrides, protectedId);
    const patches = changed.map((c) => ({ ...c }));
    for (const [id, fields] of Object.entries(overrides)) {
      if (!patches.some((p) => p.id === id) && !overflow.some((b) => b.id === id)) patches.push({ id, ...fields });
    }

    // Anything that no longer fits goes to tomorrow, remembering where it was.
    let dayAfter = withPatches(persistedSurfaceItems, patches);
    for (const block of overflow) {
      const length = overrides[block.id]?.duration_minutes ?? block.duration_minutes;
      const start = rollOverStart(dayAfter, tomorrowKey, length, endTime);
      const patch = { id: block.id, date: tomorrowKey, start_time: start, duration_minutes: length,
        displaced_from: viewKey, displaced_start: block.start_time };
      patches.push(patch);
      dayAfter = withPatches(dayAfter, [patch]);
    }

    // Anything sent away earlier comes back once the day has room for it again.
    for (const block of displacedBlocks) {
      const current = dayAfter.find((i) => i.id === block.id);
      const back = returnSlot(dayAfter, viewKey, current, block.displaced_start || block.start_time, endTime);
      if (!back) continue;
      const patch = { id: block.id, date: viewKey, start_time: back, displaced_from: null, displaced_start: null };
      patches.push(patch);
      dayAfter = withPatches(dayAfter, [patch]);
    }
    if (!patches.length) return 0;

    const before = patches
      .map((patch) => persistedSurfaceItems.find((i) => i.id === patch.id))
      .filter(Boolean)
      .filter((b, i, all) => all.findIndex((x) => x.id === b.id) === i)
      .map((b) => ({ id: b.id, date: b.date, start_time: b.start_time, duration_minutes: b.duration_minutes,
        displaced_from: b.displaced_from || null, displaced_start: b.displaced_start || null }));

    const merged = [...patches.reduce((map, p) => map.set(p.id, { ...(map.get(p.id) || {}), ...p }), new Map()).values()];
    updateDayBlocks(merged);

    const newlyOut = overflow.filter((b) => !displacedIds.has(b.id));
    if (newlyOut.length) {
      setUndoSnapshot(before);
      setNoticeHidden(false);
    } else {
      setUndoSnapshot(null);
    }
    return newlyOut.length;
  };

  const applyLength = (blockId, minutes) =>
    reflowDay(dayEnd, { [blockId]: { duration_minutes: minutes } }, blockId);

  const undoMove = () => {
    if (!undoSnapshot) return;
    updateDayBlocks(undoSnapshot);
    setUndoSnapshot(null);
  };

  const startResize = (block, event) => {
    event.preventDefault();
    event.stopPropagation();
    const startY = event.clientY;
    const startMinutes = block.duration_minutes;
    // How far this block can stretch before it meets an event or the end of the day.
    const longest = maxLengthFor(persistedSurfaceItems, viewKey, block, dayEnd);
    const lengthAt = (clientY) =>
      Math.min(longest, Math.max(MIN_BLOCK_MINUTES, snap(startMinutes + (clientY - startY) / PX_PER_MIN)));
    setResizing({ id: block.id, minutes: startMinutes, limit: longest });

    const onMove = (moveEvent) => setResizing({ id: block.id, minutes: lengthAt(moveEvent.clientY), limit: longest });
    const onUp = (upEvent) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const minutes = lengthAt(upEvent.clientY);
      setResizing(null);
      if (minutes !== startMinutes) applyLength(block.id, minutes);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const startDayEndDrag = (event) => {
    event.preventDefault();
    const startY = event.clientY;
    const startMinutes = timeToMinutes(dayEnd);
    let latest = dayEnd;
    const onMove = (moveEvent) => {
      const minutes = Math.max(60, Math.min(24 * 60 - 15, snap(startMinutes + (moveEvent.clientY - startY) / PX_PER_MIN)));
      latest = minsToTime(minutes);
      setDayEnd(viewKey, latest);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Whatever no longer fits before the new end of day goes to tomorrow.
      if (latest !== dayEnd) reflowDay(latest);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const blockMinutes = (block) => (resizing?.id === block.id ? resizing.minutes : block.duration_minutes);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "6px 10px 10px" }}>
      <DaySwitch showTomorrow={showTomorrow} onChange={(v) => { setShowTomorrow(v); setAdding(null); }} tomorrowHasPlan={tomorrowHasPlan} />

      {previewing && (
        <div style={{ marginBottom: 8, padding: "7px 8px", borderRadius: 9, background: "#F8FAFC",
          border: "1px dashed #94A3B8", fontFamily: "'DM Sans',sans-serif" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#334155" }}>
            ✦ Lumen's plan for {isToday ? "today" : "tomorrow"}
          </div>
          <div style={{ fontSize: 9, color: "#64748B", marginTop: 2 }}>
            {proposed.length
              ? `${proposed.length} block${proposed.length === 1 ? "" : "s"} shown in grey`
              : "Nothing fits before your day ends, so it all goes to the next day"}
            {replacedCount ? ` · replaces ${replacedCount} unfinished block${replacedCount === 1 ? "" : "s"}` : ""}
            . Ask Lumen to reshuffle, or:
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button type="button" onClick={() => acceptDayPlan(planId)}
              style={{ flex: 1, padding: "4px 0", borderRadius: 7, border: "none", background: "#4DB8B5", color: "white",
                fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
              Accept
            </button>
            <button type="button" onClick={() => dismissDayPlan(planId)}
              style={{ flex: 1, padding: "4px 0", borderRadius: 7, border: "1px solid #E2E8F0", background: "white",
                color: "#64748B", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {isToday && rolledIn > 0 && (
        <div style={{ marginBottom: 6, padding: "5px 8px", borderRadius: 8, background: "#FFFBEB",
          border: "1px solid #FEF3C7", fontSize: 9, color: "#92400E", fontFamily: "'DM Sans',sans-serif" }}>
          {rolledIn === 1 ? "1 unfinished block moved here" : `${rolledIn} unfinished blocks moved here`} from an earlier day.
        </div>
      )}

      {displacedBlocks.length > 0 && !noticeHidden && (
        <div style={{ marginBottom: 6, padding: "5px 8px", borderRadius: 8, background: "#EFF6FF",
          border: "1px solid #BFDBFE", fontSize: 9, color: "#1E3A8A", fontFamily: "'DM Sans',sans-serif",
          display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ flex: 1 }}>
            {displacedBlocks.length === 1
              ? `"${displacedBlocks[0].title}" is on tomorrow — it didn't fit today. Make room and it comes back.`
              : `${displacedBlocks.length} blocks are on tomorrow — they didn't fit today. Make room and they come back.`}
          </span>
          {undoSnapshot && (
            <button onClick={undoMove}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 9,
                fontWeight: 700, color: "#1D4ED8", fontFamily: "'DM Sans',sans-serif" }}>
              Undo
            </button>
          )}
          <button onClick={() => setNoticeHidden(true)} title="Dismiss"
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 10,
              color: "#64748B", lineHeight: 1 }}>
            ✕
          </button>
        </div>
      )}

      {items.length === 0 && !adding && (
        <div style={{ padding: "10px 8px", fontSize: 11, color: "#94A3B8", fontStyle: "italic", fontFamily: "'DM Sans',sans-serif" }}>
          {isToday
            ? "Nothing here yet. Add a task or a break, and today's events and dated notes will show up too."
            : "Nothing planned for tomorrow yet. Add a task, or ask Lumen to plan it."}
        </div>
      )}

      {items.map((item, index) => {
        const nowLine = markersBefore(index);

        if (item.kind === "event") {
          return (
            <div key={item.key}>
              {nowLine}
              <Row time={item.time} colors={item.ghost ? GHOST_EVENT_COLOR : EVENT_COLOR}
                title={item.ghost ? "Lumen suggested this. Confirm it on the calendar." : "Events live on the calendar"}
                height={heightForMinutes(item.minutes)}>
                <div style={{ fontSize: 11, fontWeight: 600, color: item.ghost ? "#64748B" : "#1E293B", fontFamily: "'DM Sans',sans-serif" }}>{item.title}</div>
                <div style={{ fontSize: 9, color: "#94A3B8", fontFamily: "'DM Sans',sans-serif" }}>📅 {item.detail}</div>
              </Row>
            </div>
          );
        }

        if (item.kind === "plan") {
          const pb = item.block;
          return (
            <div key={item.key}>
              {nowLine}
              <div style={{ borderRadius: 10, border: "1.5px dashed #94A3B8", marginBottom: 4 }}>
                <Row time={item.time} colors={PLAN_COLOR} title="Part of Lumen's plan. Accept it to make it real."
                  height={heightForMinutes(pb.duration_minutes) - 3}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", fontFamily: "'DM Sans',sans-serif",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pb.title}</div>
                  <div style={{ fontSize: 9, color: "#94A3B8", fontFamily: "'DM Sans',sans-serif" }}>
                    {pb.type === "break" ? "☕" : "✓"} {pb.duration_minutes} min · suggested
                  </div>
                </Row>
              </div>
            </div>
          );
        }

        if (item.kind === "note") {
          return (
            <div key={item.key}>
              {nowLine}
              <Row time={item.time} colors={NOTE_COLOR} faded={item.done} title="Notes live on the bulletin board">
                <div style={{ fontSize: 11, fontWeight: 600, color: "#1E293B", fontFamily: "'DM Sans',sans-serif",
                  textDecoration: item.done ? "line-through" : "none" }}>{item.title}</div>
                <div style={{ fontSize: 9, color: "#94A3B8", fontFamily: "'DM Sans',sans-serif" }}>📌 {item.detail}</div>
              </Row>
            </div>
          );
        }

        const b = item.block;
        const colors = TYPE_COLORS[b.type] || TYPE_COLORS.task;
        const isActive = b.id === activeBlockId;
        const minutes = blockMinutes(b);
        return (
          <div key={item.key}>
            {nowLine}
            <div onMouseEnter={() => setHoveredId(b.id)} onMouseLeave={() => setHoveredId(null)}
              style={{ position: "relative" }}>
              <Row time={item.time} colors={colors} onClick={() => onActivateBlock?.(isActive ? null : b.id)}
                title={isActive ? "Stop this block" : "Start this block"}
                faded={b.done} height={heightForMinutes(minutes)}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button title={b.done ? "Mark as not done" : "Mark as done"}
                    onClick={(e) => { e.stopPropagation(); toggleDayBlock(b.id); }}
                    style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0, cursor: "pointer",
                      border: `1.5px solid ${b.done ? colors.border : "#CBD5E1"}`,
                      background: b.done ? colors.border : "white", color: "white", fontSize: 9, lineHeight: 1,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                    {b.done ? "✓" : ""}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: "#1E293B",
                      fontFamily: "'DM Sans',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      textDecoration: b.done ? "line-through" : "none" }}>
                      {b.title}
                    </div>
                    <div style={{ fontSize: 9, color: "#94A3B8", fontFamily: "'DM Sans',sans-serif" }}>
                      {b.type === "break" ? "☕" : "✓"} {minutes} min
                      {resizing?.id === b.id && resizing.minutes >= resizing.limit && (
                        <span style={{ marginLeft: 6, color: "#B45309", fontWeight: 700 }}>· as long as it can go</span>
                      )}
                    </div>
                  </div>
                  {isActive && countdown && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                      <BlockPie frac={countdown.secsLeft / (minutes * 60)} color={colors.border} size={22} />
                      <span style={{ fontSize: 10, fontWeight: 700, color: colors.border, fontFamily: "'DM Sans',sans-serif" }}>
                        {countdown.fmtTime(countdown.secsLeft)}
                      </span>
                    </div>
                  )}
                </div>
              </Row>
              {hoveredId === b.id && (
                <button title="Remove this block"
                  onClick={(e) => { e.stopPropagation(); if (isActive) onActivateBlock?.(null); deleteDayBlock(b.id); }}
                  style={{ position: "absolute", top: 4, right: 4, width: 16, height: 16, borderRadius: "50%",
                    border: "none", background: "rgba(0,0,0,0.12)", color: "#475569", fontSize: 9, fontWeight: 700,
                    cursor: "pointer", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  ✕
                </button>
              )}
              <div title="Drag to change how long this takes" onMouseDown={(e) => startResize(b, e)}
                style={{ position: "absolute", left: 42, right: 6, bottom: 1, height: 8, cursor: "ns-resize",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 22, height: 2, borderRadius: 1,
                  background: hoveredId === b.id || resizing?.id === b.id ? colors.border : "transparent" }} />
              </div>
            </div>
          </div>
        );
      })}

      {markersBefore(-1)}

      {adding && <BlockForm type={adding} onSave={addBlock} onCancel={() => setAdding(null)} />}

      {!adding && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <button onClick={() => setAdding("task")}
            style={{ flex: 1, padding: "5px 0", borderRadius: 8, border: `1px dashed ${TYPE_COLORS.task.border}66`,
              background: TYPE_COLORS.task.bg, color: "#166534", fontSize: 10, fontWeight: 700,
              cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
            + Task
          </button>
          <button onClick={() => setAdding("break")}
            style={{ flex: 1, padding: "5px 0", borderRadius: 8, border: `1px dashed ${TYPE_COLORS.break.border}`,
              background: TYPE_COLORS.break.bg, color: "#475569", fontSize: 10, fontWeight: 700,
              cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
            + Break
          </button>
        </div>
      )}
    </div>
  );
}
